// ── HUD Layout Editor ──
// Allows mobile players to drag and resize touch button positions.
// Supports preset layouts (2-Thumb, 3-Finger Claw, 4-Finger Claw),
// grid snapping, and persistent localStorage saving.

import type { TouchButtons } from '../input/touch-buttons';
import { isMobile } from '../core/device';

// ── Types ──

/** Position and size record for each button by ID. */
export type ButtonLayout = Record<string, { x: number; y: number; size: number }>;

export type PresetName = 'default' | '3-finger' | '4-finger';

// ── Constants ──

/** Grid snap size in pixels. */
export const GRID_SIZE = 8;

/** localStorage key for persisting the HUD layout. */
export const HUD_LAYOUT_STORAGE_KEY = 'cp-hud-layout';

/** F-MOB-M5 / MOB-W10: localStorage key for the per-button visibility set.
 *  Stored as a JSON array of button IDs that should be HIDDEN. */
export const HUD_LAYOUT_HIDDEN_KEY = 'cp-hud-layout-hidden';

/** Snap a value to the nearest grid increment. */
export function snapToGrid(value: number): number {
  return Math.round(value / GRID_SIZE) * GRID_SIZE;
}

/** F-MOB-M5 / MOB-W10: read the persisted hidden-button id set. Returns an
 *  empty Set when nothing is saved or the payload fails the basic shape check. */
export function loadHiddenButtons(): Set<string> {
  try {
    if (typeof localStorage === 'undefined') return new Set();
    const raw = localStorage.getItem(HUD_LAYOUT_HIDDEN_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    const out = new Set<string>();
    for (const v of parsed) if (typeof v === 'string') out.add(v);
    return out;
  } catch {
    return new Set();
  }
}

/** F-MOB-M5 / MOB-W10: persist the hidden-button id set. */
export function saveHiddenButtons(ids: Iterable<string>): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(HUD_LAYOUT_HIDDEN_KEY, JSON.stringify([...ids]));
  } catch {
    // localStorage full or unavailable.
  }
}

/**
 * Read + parse the persisted HUD layout from localStorage.
 *
 * Used at startup by `TouchButtons.createButtons()` so that customizations
 * the user made via the editor (F9 on desktop / long-press on mobile) survive
 * a page reload. Returns `null` when nothing is saved or the stored payload
 * fails the basic shape check (object with per-button {x, y, size} entries).
 *
 * Validation is intentionally permissive — extra fields and missing buttons
 * are tolerated; we only reject obviously malformed data so a corrupt key
 * never crashes the boot path.
 */
export function loadSavedLayout(): ButtonLayout | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(HUD_LAYOUT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;

    const out: ButtonLayout = {};
    for (const [id, entry] of Object.entries(parsed as Record<string, unknown>)) {
      if (
        entry &&
        typeof entry === 'object' &&
        typeof (entry as { x?: unknown }).x === 'number' &&
        typeof (entry as { y?: unknown }).y === 'number' &&
        typeof (entry as { size?: unknown }).size === 'number'
      ) {
        const e = entry as { x: number; y: number; size: number };
        out[id] = { x: e.x, y: e.y, size: e.size };
      }
    }
    return out;
  } catch {
    return null;
  }
}

// ── Preset Layouts ──
// Positions are in viewport-relative pixels for a 16:9 landscape layout.
// The editor normalizes to actual viewport on apply.

const BUTTON_IDS = ['btn-fire', 'btn-ads', 'btn-reload', 'btn-jump', 'btn-crouch', 'btn-weapon'] as const;

export const PRESET_LAYOUTS: Record<PresetName, ButtonLayout> = {
  /** Default 2-thumb layout: buttons hugging screen edges. */
  'default': {
    'btn-fire':   { x: 88, y: 70, size: 64 },   // right side, lower
    'btn-ads':    { x: 88, y: 50, size: 48 },   // right side, mid
    'btn-reload': { x: 88, y: 35, size: 44 },   // right side, upper-mid
    'btn-jump':   { x: 25, y: 85, size: 48 },   // left side, bottom
    'btn-crouch': { x: 35, y: 85, size: 48 },   // left-center, bottom
    'btn-weapon': { x: 93, y: 20, size: 44 },   // right edge, top area
  },
  /** 3-finger claw: fire button moved to top-right for index finger. */
  '3-finger': {
    'btn-fire':   { x: 85, y: 8, size: 64 },    // top-right corner
    'btn-ads':    { x: 88, y: 50, size: 48 },   // right mid
    'btn-reload': { x: 88, y: 35, size: 44 },   // right upper-mid
    'btn-jump':   { x: 25, y: 85, size: 48 },   // left bottom
    'btn-crouch': { x: 35, y: 85, size: 48 },   // left-center bottom
    'btn-weapon': { x: 93, y: 20, size: 44 },   // right edge
  },
  /** 4-finger claw: fire + ADS in top corners for index fingers. */
  '4-finger': {
    'btn-fire':   { x: 85, y: 8, size: 64 },    // top-right
    'btn-ads':    { x: 8, y: 8, size: 56 },     // top-left
    'btn-reload': { x: 88, y: 40, size: 44 },   // right mid
    'btn-jump':   { x: 25, y: 85, size: 48 },   // left bottom
    'btn-crouch': { x: 35, y: 85, size: 48 },   // left-center bottom
    'btn-weapon': { x: 93, y: 25, size: 44 },   // right edge
  },
};

// ── Editor ──

export class HudLayoutEditor {
  private buttons: TouchButtons;
  private editing = false;
  private overlay: HTMLDivElement | null = null;
  private currentLayout: ButtonLayout = { ...PRESET_LAYOUTS['default'] };
  /** F-MOB-M5 / MOB-W10: the set of button IDs the user has hidden. */
  private hiddenButtons: Set<string> = loadHiddenButtons();
  private dragState: {
    buttonId: string;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    touchId: number;
  } | null = null;
  private resizeState: {
    buttonId: string;
    initialDistance: number;
    origSize: number;
    /** BUG-HUD-29: identifiers of the two fingers contributing to the pinch.
     *  Recorded on-the-fly when the second finger arrives via touchmove. */
    touchIds: [number, number];
  } | null = null;

  // Bound handlers for cleanup
  private readonly handleTouchMove: (e: TouchEvent) => void;
  private readonly handleTouchEnd: (e: TouchEvent) => void;
  private readonly handleDragStart: (e: Event) => void;

  /** AbortController for UI button listeners (preset/reset/done). */
  private uiAbortController: AbortController | null = null;

  /** AbortController for layout-mode listeners (per-button drag + global document). */
  private layoutAbortController: AbortController | null = null;

  /** BUG-HUD-13: AbortController for the mobile long-press activation gesture
   *  on the #hud element. Detached on dispose(). */
  private activationAbortController: AbortController | null = null;

  /** BUG-HUD-13: pending long-press timeout id (3-finger 1s hold). */
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;

  /** BUG-HUD-13: long-press duration in ms. */
  private static readonly LONG_PRESS_MS = 1000;

  /** BUG-HUD-13: required simultaneous touch count to trigger entry. */
  private static readonly LONG_PRESS_FINGERS = 3;

  constructor(buttons: TouchButtons) {
    this.buttons = buttons;
    this.handleTouchMove = this.onTouchMove.bind(this);
    this.handleTouchEnd = this.onTouchEnd.bind(this);
    this.handleDragStart = this.onDragStart.bind(this) as EventListener;

    // Try to load saved layout on construction
    const saved = this.loadLayout();
    if (saved) {
      this.currentLayout = saved;
    }

    // BUG-HUD-13: attach the mobile 3-finger long-press activation gesture
    // on the #hud root so players have a documented entry point. The settings
    // menu (or game.ts) can also call enable() directly.
    this.attachActivationGesture();
  }

  /** Whether the editor is currently in edit mode. */
  isEditing(): boolean {
    return this.editing;
  }

  /**
   * BUG-HUD-13: Public activation entry point. The settings menu, game.ts,
   * or any external trigger can call this to open the layout editor.
   *
   * Equivalent to {@link enterEditMode} but exposed under a more obvious
   * name for the "open HUD editor" UX action. Idempotent.
   */
  enable(): void {
    this.enterEditMode();
  }

  /**
   * BUG-HUD-13: Attach the mobile 3-finger long-press activation gesture on
   * the `#hud` element. Holding 3 fingers on the HUD for 1 second opens the
   * layout editor.
   *
   * No-op on desktop (uses {@link isMobile}) and when `document` / `#hud`
   * are unavailable (test / SSR contexts). Listeners are tracked via an
   * AbortController so `dispose()` can detach them cleanly.
   */
  private attachActivationGesture(): void {
    if (typeof document === 'undefined') return;
    if (typeof document.getElementById !== 'function') return;
    if (!isMobile()) return;

    const hud = document.getElementById('hud');
    if (!hud || typeof hud.addEventListener !== 'function') return;

    this.activationAbortController = new AbortController();
    const signal = this.activationAbortController.signal;

    const clearTimer = () => {
      if (this.longPressTimer !== null) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
    };

    const onStart = (e: TouchEvent) => {
      // Don't re-trigger while already editing.
      if (this.editing) return;
      if (e.touches.length !== HudLayoutEditor.LONG_PRESS_FINGERS) {
        clearTimer();
        return;
      }
      clearTimer();
      this.longPressTimer = setTimeout(() => {
        this.longPressTimer = null;
        // Re-check finger count just before firing — drop if user lifted early.
        // (We can't read live touches here; the cancel/end handlers below
        // will have cleared the timer if the user lifted.)
        this.enable();
      }, HudLayoutEditor.LONG_PRESS_MS);
    };

    const onMoveOrEnd = (e: TouchEvent) => {
      if (e.touches.length !== HudLayoutEditor.LONG_PRESS_FINGERS) {
        clearTimer();
      }
    };

    hud.addEventListener('touchstart', onStart as EventListener, { signal, passive: true });
    hud.addEventListener('touchmove', onMoveOrEnd as EventListener, { signal, passive: true });
    hud.addEventListener('touchend', onMoveOrEnd as EventListener, { signal });
    hud.addEventListener('touchcancel', onMoveOrEnd as EventListener, { signal });
  }

  /** Enter edit mode: show grid overlay and make buttons draggable. */
  enterEditMode(): void {
    if (this.editing) return;
    this.editing = true;

    // Create the edit overlay with grid background
    this.overlay = document.createElement('div');
    this.overlay.id = 'hud-layout-editor-overlay';
    Object.assign(this.overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      zIndex: '2000',
      pointerEvents: 'none',
      backgroundImage: `
        linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
      `,
      backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`,
    });

    // Add instruction bar
    const bar = document.createElement('div');
    Object.assign(bar.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      padding: '8px 16px',
      background: 'rgba(0,0,0,0.8)',
      color: '#fff',
      fontFamily: 'sans-serif',
      fontSize: '14px',
      textAlign: 'center',
      zIndex: '2001',
      pointerEvents: 'auto',
    });
    bar.textContent = 'Drag buttons to reposition. Pinch to resize. Tap preset buttons below.';
    this.overlay.appendChild(bar);

    // Add preset buttons
    const presetBar = document.createElement('div');
    Object.assign(presetBar.style, {
      position: 'absolute',
      bottom: '0',
      left: '0',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      gap: '12px',
      padding: '12px',
      background: 'rgba(0,0,0,0.8)',
      zIndex: '2001',
      pointerEvents: 'auto',
    });

    const presetNames: Array<{ key: PresetName; label: string }> = [
      { key: 'default', label: '2-Thumb' },
      { key: '3-finger', label: '3-Finger Claw' },
      { key: '4-finger', label: '4-Finger Claw' },
    ];

    // Use AbortController so all UI button listeners are removed on exit
    this.uiAbortController = new AbortController();
    const signal = this.uiAbortController.signal;

    for (const { key, label } of presetNames) {
      const btn = document.createElement('button');
      btn.textContent = label;
      Object.assign(btn.style, {
        padding: '8px 16px',
        background: 'rgba(255,255,255,0.15)',
        border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: '4px',
        color: '#fff',
        fontFamily: 'sans-serif',
        fontSize: '12px',
        cursor: 'pointer',
      });
      // BUG-HUD-12: single pointerdown listener (no touchstart+click double-fire).
      btn.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        this.applyPreset(key);
      }, { signal });
      presetBar.appendChild(btn);
    }

    // Reset button
    const resetBtn = document.createElement('button');
    resetBtn.textContent = 'Reset';
    Object.assign(resetBtn.style, {
      padding: '8px 16px',
      background: 'rgba(255,80,80,0.3)',
      border: '1px solid rgba(255,80,80,0.5)',
      borderRadius: '4px',
      color: '#fff',
      fontFamily: 'sans-serif',
      fontSize: '12px',
      cursor: 'pointer',
    });
    // BUG-HUD-12: single pointerdown listener (no touchstart+click double-fire).
    resetBtn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.applyPreset('default');
    }, { signal });
    presetBar.appendChild(resetBtn);

    // Done button
    const doneBtn = document.createElement('button');
    doneBtn.textContent = 'Done';
    Object.assign(doneBtn.style, {
      padding: '8px 16px',
      background: 'rgba(80,255,80,0.3)',
      border: '1px solid rgba(80,255,80,0.5)',
      borderRadius: '4px',
      color: '#fff',
      fontFamily: 'sans-serif',
      fontSize: '12px',
      cursor: 'pointer',
    });
    // BUG-HUD-12: single pointerdown listener (no touchstart+click double-fire).
    doneBtn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.exitEditMode();
    }, { signal });
    presetBar.appendChild(doneBtn);

    this.overlay.appendChild(presetBar);
    document.body.appendChild(this.overlay);

    // Make button elements draggable
    this.addDragHandlers();
  }

  /** Exit edit mode: save layout and remove overlay. */
  exitEditMode(): void {
    if (!this.editing) return;
    this.editing = false;
    this.saveLayout();
    this.removeDragHandlers();

    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }

  /** Apply a named preset layout and save. */
  applyPreset(preset: PresetName): void {
    const layout = PRESET_LAYOUTS[preset];
    if (!layout) return;

    this.currentLayout = { ...layout };
    this.applyLayoutToButtons();
    this.saveLayout();
  }

  /** Save the current layout to localStorage. */
  saveLayout(): void {
    try {
      localStorage.setItem(HUD_LAYOUT_STORAGE_KEY, JSON.stringify(this.currentLayout));
    } catch {
      // localStorage full or unavailable
    }
  }

  /** Load a saved layout from localStorage. */
  loadLayout(): ButtonLayout | null {
    try {
      const raw = localStorage.getItem(HUD_LAYOUT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as ButtonLayout;
        // Validate basic structure
        if (typeof parsed === 'object' && parsed !== null) {
          return parsed;
        }
      }
    } catch {
      // Corrupted data
    }
    return null;
  }

  /** Clean up all resources. */
  dispose(): void {
    if (this.editing) {
      this.exitEditMode();
    }
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    // BUG-HUD-13: detach the activation gesture + clear any pending timer.
    if (this.activationAbortController) {
      this.activationAbortController.abort();
      this.activationAbortController = null;
    }
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  // ── Private ──

  /** Apply the current layout record to actual button DOM elements. */
  private applyLayoutToButtons(): void {
    const container = (this.buttons as any).getContainer?.();
    if (!container) return;

    const buttons = container.querySelectorAll?.('[id^="btn-"]') ?? container.children ?? [];
    for (const btn of buttons) {
      const el = btn as HTMLElement;
      const layoutEntry = this.currentLayout[el.id];
      if (layoutEntry) {
        el.style.left = `${layoutEntry.x}%`;
        el.style.top = `${layoutEntry.y}%`;
        el.style.width = `${layoutEntry.size}px`;
        el.style.height = `${layoutEntry.size}px`;
        // Remove any right/bottom positioning that conflicts
        el.style.right = '';
        el.style.bottom = '';
      }
      // F-MOB-M5 / MOB-W10: enforce the per-button visibility set. While the
      // editor is open, hidden buttons are dimmed instead so the user can
      // toggle them back on; once the editor closes we hide them outright.
      if (this.hiddenButtons.has(el.id)) {
        if (this.editing) {
          el.style.opacity = '0.25';
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      } else {
        el.style.display = '';
        el.style.opacity = '';
      }
    }
  }

  /** F-MOB-M5 / MOB-W10: toggle whether a button is hidden + persist. */
  toggleButtonHidden(id: string): boolean {
    if (this.hiddenButtons.has(id)) this.hiddenButtons.delete(id);
    else this.hiddenButtons.add(id);
    saveHiddenButtons(this.hiddenButtons);
    this.applyLayoutToButtons();
    return this.hiddenButtons.has(id);
  }

  /** F-MOB-M5: read-only accessor for the hidden set (used by external
   *  rendering paths that need to skip the same buttons). */
  getHiddenButtons(): ReadonlySet<string> {
    return this.hiddenButtons;
  }

  /** Add touch event handlers for drag & resize. */
  private addDragHandlers(): void {
    const container = (this.buttons as any).getContainer?.();
    if (!container) return;

    // UI-H4: AbortController for all layout-mode listeners
    // (per-button touchstart + global document touchmove/end/cancel).
    this.layoutAbortController = new AbortController();
    const signal = this.layoutAbortController.signal;

    const buttons = container.querySelectorAll?.('[id^="btn-"]') ?? container.children ?? [];
    for (const btn of buttons) {
      const el = btn as HTMLElement;
      // Add drag handle visual
      el.style.border = '2px dashed rgba(0,200,255,0.7)';
      el.style.pointerEvents = 'auto';
      el.addEventListener('touchstart', this.handleDragStart, { signal });

      // F-MOB-M5 / MOB-W10: per-button visibility toggle. A small "eye" overlay
      // sits in the top-right of each button; tapping it flips hidden state in
      // the persisted set + re-renders.
      this.addVisibilityToggle(el, signal);
    }

    document.addEventListener('touchmove', this.handleTouchMove, { passive: false, signal });
    document.addEventListener('touchend', this.handleTouchEnd, { signal });
    document.addEventListener('touchcancel', this.handleTouchEnd, { signal });

    // F-MOB-M5 / MOB-W10: apply persisted hidden state immediately so the
    // editor reflects what's on disk.
    this.applyLayoutToButtons();
  }

  /** F-MOB-M5 / MOB-W10: add an eye icon on a button that toggles its hidden state. */
  private addVisibilityToggle(parent: HTMLElement, signal: AbortSignal): void {
    if (typeof document === 'undefined' || typeof document.createElement !== 'function') return;
    const dot = document.createElement('div');
    dot.className = 'hud-editor-vis-toggle';
    dot.textContent = this.hiddenButtons.has(parent.id) ? 'OFF' : 'ON';
    Object.assign(dot.style, {
      position: 'absolute',
      top: '-10px',
      right: '-10px',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      background: this.hiddenButtons.has(parent.id) ? 'rgba(255,80,80,0.9)' : 'rgba(80,180,80,0.9)',
      color: '#fff',
      fontSize: '8px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      pointerEvents: 'auto',
      zIndex: '2002',
    } satisfies Partial<CSSStyleDeclaration>);

    const handle = (ev: Event) => {
      ev.preventDefault();
      ev.stopPropagation();
      const isHidden = this.toggleButtonHidden(parent.id);
      dot.textContent = isHidden ? 'OFF' : 'ON';
      dot.style.background = isHidden ? 'rgba(255,80,80,0.9)' : 'rgba(80,180,80,0.9)';
    };
    // BUG-HUD-12: single pointerdown listener (no touchstart+click double-fire).
    dot.addEventListener('pointerdown', handle, { signal });

    // Ensure the parent is positioned so the absolute child anchors correctly.
    if (!parent.style.position) parent.style.position = 'absolute';
    parent.appendChild?.(dot);

    // Track the dot so removeDragHandlers can clean it up.
    if (!this.visibilityDots) this.visibilityDots = [];
    this.visibilityDots.push(dot);
  }

  /** F-MOB-M5 / MOB-W10: track visibility-toggle dots so we can detach them. */
  private visibilityDots: HTMLElement[] | null = null;

  /** Remove drag handlers via AbortController + abort UI button listeners. */
  private removeDragHandlers(): void {
    // UI-H4: abort layout-mode listeners (per-button drag + global document)
    if (this.layoutAbortController) {
      this.layoutAbortController.abort();
      this.layoutAbortController = null;
    }

    // Abort all UI button listeners (preset/reset/done) (BUG-18 fix)
    if (this.uiAbortController) {
      this.uiAbortController.abort();
      this.uiAbortController = null;
    }

    // F-MOB-M5 / MOB-W10: detach the per-button visibility-toggle dots.
    if (this.visibilityDots) {
      for (const d of this.visibilityDots) d.remove?.();
      this.visibilityDots = null;
    }

    // Re-apply layout once more so hidden buttons collapse to display:none
    // now that we're out of edit mode.
    this.applyLayoutToButtons();
  }

  /** Handle drag start on a button. */
  private onDragStart(e: TouchEvent): void {
    if (!this.editing) return;
    const target = e.currentTarget as HTMLElement;
    if (!target.id) return;

    if (e.touches.length === 2) {
      // Pinch-to-resize
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      this.resizeState = {
        buttonId: target.id,
        initialDistance: dist,
        origSize: this.currentLayout[target.id]?.size ?? 64,
        touchIds: [e.touches[0].identifier, e.touches[1].identifier],
      };
      this.dragState = null;
      return;
    }

    if (e.touches.length === 1) {
      e.preventDefault();
      const touch = e.touches[0];
      const rect = target.getBoundingClientRect();
      this.dragState = {
        buttonId: target.id,
        startX: touch.clientX,
        startY: touch.clientY,
        origX: rect.left,
        origY: rect.top,
        touchId: touch.identifier,
      };
      this.resizeState = null;
    }
  }

  /** Handle touch move for dragging or resizing. */
  private onTouchMove(e: TouchEvent): void {
    // BUG-HUD-29: detect second-finger arrival during a drag and promote
    // to pinch-resize. The first touchstart only fires once, so iOS/Android
    // often deliver the second finger via touchmove with touches.length === 2.
    // Record the second touch identifier on-the-fly.
    if (!this.resizeState && this.dragState && e.touches.length === 2) {
      e.preventDefault();
      const buttonId = this.dragState.buttonId;
      const t0 = e.touches[0];
      const t1 = e.touches[1];
      const dx0 = t0.clientX - t1.clientX;
      const dy0 = t0.clientY - t1.clientY;
      const dist0 = Math.sqrt(dx0 * dx0 + dy0 * dy0);
      this.resizeState = {
        buttonId,
        initialDistance: dist0 || 1, // avoid divide-by-zero on coincident touches
        origSize: this.currentLayout[buttonId]?.size ?? 64,
        touchIds: [t0.identifier, t1.identifier],
      };
      this.dragState = null;
      return;
    }

    if (this.resizeState && e.touches.length >= 2) {
      e.preventDefault();
      // BUG-HUD-29: prefer the originally-tracked touch identifiers so a
      // third finger or finger-swap doesn't corrupt the pinch geometry.
      const [idA, idB] = this.resizeState.touchIds;
      let tA: Touch | null = null;
      let tB: Touch | null = null;
      for (let i = 0; i < e.touches.length; i++) {
        const t = e.touches[i];
        if (t.identifier === idA) tA = t;
        else if (t.identifier === idB) tB = t;
      }
      // Fall back to the first two touches if either tracked id is gone
      // (e.g. user lifted one finger but immediately set down another).
      if (!tA) tA = e.touches[0];
      if (!tB) tB = e.touches[1];

      const dx = tA.clientX - tB.clientX;
      const dy = tA.clientY - tB.clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const scale = dist / this.resizeState.initialDistance;
      const newSize = Math.max(32, Math.min(96, Math.round(this.resizeState.origSize * scale)));

      if (this.currentLayout[this.resizeState.buttonId]) {
        this.currentLayout[this.resizeState.buttonId].size = snapToGrid(newSize);
        this.applyLayoutToButtons();
      }
      return;
    }

    if (this.dragState && e.touches.length === 1) {
      e.preventDefault();
      const touch = e.touches[0];
      if (touch.identifier !== this.dragState.touchId) return;

      const vw = window.innerWidth || 800;
      const vh = window.innerHeight || 600;

      const deltaX = touch.clientX - this.dragState.startX;
      const deltaY = touch.clientY - this.dragState.startY;

      const newLeft = this.dragState.origX + deltaX;
      const newTop = this.dragState.origY + deltaY;

      // Convert to percentage of viewport
      const pctX = snapToGrid(Math.round((newLeft / vw) * 100));
      const pctY = snapToGrid(Math.round((newTop / vh) * 100));

      // Clamp to 0-95%
      const clampedX = Math.max(0, Math.min(95, pctX));
      const clampedY = Math.max(0, Math.min(95, pctY));

      if (this.currentLayout[this.dragState.buttonId]) {
        this.currentLayout[this.dragState.buttonId].x = clampedX;
        this.currentLayout[this.dragState.buttonId].y = clampedY;
      } else {
        this.currentLayout[this.dragState.buttonId] = { x: clampedX, y: clampedY, size: 64 };
      }

      this.applyLayoutToButtons();
    }
  }

  /** Handle touch end — finalize drag/resize. */
  private onTouchEnd(_e: TouchEvent): void {
    this.dragState = null;
    this.resizeState = null;
  }
}
